import React from "react"
import {
  ActionType,
  BrandLogoBlock,
  NavTab,
  SideNavFrame,
  useSideNavCollapse,
} from "../side-nav"

// Side navigation glyphs from the design system (currentColor).
import { ReactComponent as AssetsIcon } from "assets/svg/side-nav/assets.svg"
import { ReactComponent as DashboardIcon } from "assets/svg/side-nav/dashboard.svg"
import { ReactComponent as DisciplinaryIcon } from "assets/svg/side-nav/disciplinary.svg"
import { ReactComponent as DocumentsIcon } from "assets/svg/side-nav/documents.svg"
import { ReactComponent as PerformanceIcon } from "assets/svg/side-nav/performance.svg"
import { ReactComponent as RequestIcon } from "assets/svg/side-nav/request.svg"
import { ReactComponent as TaskIcon } from "assets/svg/side-nav/task.svg"
import {
  CurrencyDollarIcon,
  DocumentIcon,
  GiftIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline"

import { Squares2X2Icon } from "@heroicons/react/24/solid"
import { useAuthStore } from "stores"
import { isPadeDomain } from "utils/utils"
import { useCustomizationSettingsStore } from "stores/customization-settings"
import PadeLogoWhite from "assets/logos/paidhr-logo-white.svg"

export const SideNavigationBar: React.FC<{
  isSidebarOpen: boolean
  toggleSidebar: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ isSidebarOpen, toggleSidebar }) => {
  const { compact } = useSideNavCollapse()
  const { user } = useAuthStore()
  const { customizationSettings } = useCustomizationSettingsStore()

  const actions: ActionType[] = [
    {
      title: "Job and Pay",
      icon: <CurrencyDollarIcon className="w-4" />,
      route: "/employee/dashboard/job-and-pay",
      patterns: [{ path: "/employee/dashboard/job-and-pay/*" }],
      enabled: !user?.isPayrollEnabled ? false : true,
      badge: false,
    },
    {
      title: "Personal",
      icon: <UserCircleIcon className="w-4" />,
      route: "/employee/dashboard/personal",
      patterns: [{ path: "/employee/dashboard/personal/*" }],
      enabled: true,
      badge: false,
    },
    {
      title: "Requests",
      icon: <RequestIcon className="w-4" />,
      patterns: [{ path: "/approval/requests/*" }],
      enabled: !user?.isHrisEnabled ? false : true,
      badge: false,
      subActions: [
        {
          title: "Requisitions",
          route: "/approval/requests/requisitions",
          patterns: [{ path: "/approval/requests/requisitions/*" }],
          enabled: true,
        },
      ],
    },
    // {
    //   title: "Requests",
    //   icon: <UsersIcon className="w-5" />,
    //   subActions: [
    //     {
    //       title: "Leaves",
    //       route: "request/leaves",
    //       patterns: [{ path: "/employee/dashboard/request/leaves/*" }],
    //     },
    //     {
    //       title: "Loan",
    //       route: "request/loan",
    //       patterns: [{ path: "/employee/dashboard/request/loan/*" }],
    //     },
    //     {
    //       title: "Salary Advance",
    //       route: "request/salary-advance",
    //       patterns: [{ path: "/employee/dashboard/request/salary-advance/*" }],
    //     },
    //     {
    //       title: "Documents",
    //       route: "request/documents",
    //       patterns: [{ path: "/employee/dashboard/request/documents/*" }],
    //     },
    //     {
    //       title: "Exit",
    //       route: "request/exit",
    //       patterns: [{ path: "/employee/dashboard/request/exit/*" }],
    //     },
    //   ],
    // },
    {
      title: "Documents",
      icon: <DocumentsIcon className="w-4" />,
      route: "/employee/dashboard/document",
      patterns: [{ path: "/employee/dashboard/document/*" }],
      enabled: !user?.isHrisEnabled ? false : true,
      badge: false,
    },
    {
      title: "Benefits",
      icon: <GiftIcon className="w-4" />,
      route: "/employee/dashboard/benefits",
      patterns: [{ path: "/employee/dashboard/benefits/*" }],
      enabled: !user?.isHrisEnabled ? false : true,
      badge: false,
    },
    {
      title: "Payslip",
      icon: <GiftIcon className="w-4" />,
      route: "/employee/dashboard/payslip",
      patterns: [{ path: "/employee/dashboard/payslip/*" }],
      enabled: !user?.isPayrollEnabled ? false : true,
      badge: false,
    },
    // {
    //   title: "Wallet",
    //   icon: <BanknotesIcon className="w-5" />,
    //   enabled: true,
    //   badge: false,
    //   subActions: [
    //     {
    //       title: "PaidHR Wallet",
    //       route: "wallet",
    //       patterns: [{ path: "/employee/dashboard/wallet/*" }],
    //       enabled: true,
    //     },

    //     {
    //       title: "Transactions",
    //       route: "wallet/transactions",
    //       patterns: [{ path: "/employee/dashboard/wallet/transactions/*" }],
    //       enabled: true,
    //     },
    //   ],
    // },
    {
      title: "Assets",
      icon: <AssetsIcon className="w-4" />,
      route: "/employee/dashboard/assets",
      patterns: [{ path: "/employee/dashboard/assets/*" }],
      enabled: !user?.isHrisEnabled ? false : true,
      badge: false,
    },
    {
      title: "Tasks",
      icon: <TaskIcon className="w-4" />,
      route: "/employee/dashboard/tasks",
      patterns: [{ path: "/employee/dashboard/tasks/*" }],
      enabled: !user?.isPerformanceEnabled ? false : true,
      badge: false,
    },
    {
      title: "Performance",
      icon: <PerformanceIcon className="w-4" />,
      route: "/employee/dashboard/performance",
      patterns: [{ path: "/employee/dashboard/objectives/*" }],
      enabled: !user?.isPerformanceEnabled ? false : true,
      badge: false,
    },
    {
      title: "Disciplinary",
      icon: <DisciplinaryIcon className="w-4" />,
      route: "/employee/dashboard/disciplinary",
      patterns: [{ path: "/employee/dashboard/disciplinary/*" }],
      enabled: !user?.isHrisEnabled ? false : true,
      badge: false,
    },
    /*{
      title: "Notes",
      icon: <DocumentIcon className="w-4" />,
      route: "notes",
      patterns: [{ path: "/employee/dashboard/notes/*" }],
      enabled: !user?.isHrisEnabled? false: true,
    },*/
  ]

  const handleToggleSidebar = () => {
    toggleSidebar(false)
    document.body.classList.remove("overflow-hidden")
  }

  return (
    <>
      {isSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-10 bg-black/30  cursor-pointer backdrop-blur-sm"
          onClick={handleToggleSidebar}
        />
      )}
      <SideNavFrame
        isSidebarOpen={isSidebarOpen}
        compact={compact}
        zIndexClass="z-20"
        backdrop
      >
        <div className="h-full w-full py-[18px] px-3 flex flex-col justify-start gap-3 relative z-[2]">
          {/* Brand */}
          <BrandLogoBlock
            compact={compact}
            orgName={user?.userOrganisation}
            alt=""
            src={
              isPadeDomain()
                ? PadeLogoWhite
                : customizationSettings?.brand?.menuLogoUrl
            }
          />
          {/* Menu */}
          <div className="h-full w-full flex flex-col gap-9">
            {/* Dashboard */}
            <div className="gap-0.5 flex flex-col">
              {user?.userStatusId === 5 ? (
                <NavTab
                  action={{
                    title: "Onboarding",
                    route: "welcome",
                    icon: <Squares2X2Icon className="w-4" />,
                    patterns: [{ path: "employee/dashboard/welcome" }],
                    enabled: true,
                    badge: false,
                  }}
                  toggleSidebar={handleToggleSidebar}
                  compact={compact}
                />
              ) : (
                ""
              )}

              {user?.userStatusId === 5 ? (
                ""
              ) : (
                <NavTab
                  action={{
                    title: "Dashboard",
                    route: "/employee/dashboard",
                    icon: <DashboardIcon className="w-4" />,
                    patterns: [{ path: "employee/dashboard" }],
                    enabled: true,
                    badge: false,
                  }}
                  toggleSidebar={handleToggleSidebar}
                  compact={compact}
                />
              )}
            </div>
            {
              /* Other Menu */
              user?.userStatusId === 5 ? (
                ""
              ) : (
                <div className="h-full w-full flex flex-col">
                  <div className="h-full w-full flex flex-col">
                    <div className="h-auto overflow-y-auto w-full flex flex-col gap-0.5">
                      {actions.map((action, index) =>
                        !action.enabled ? (
                          ""
                        ) : (
                          <NavTab
                            action={action}
                            key={index}
                            index={index}
                            toggleSidebar={handleToggleSidebar}
                            compact={compact}
                          />
                        )
                      )}
                    </div>
                  </div>
                </div>
              )
            }
          </div>
        </div>
      </SideNavFrame>
    </>
  )
}
